import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  QBodyLayout,
  QButton,
  QButtonGroup,
  QDateMetadataItem,
  QHeader,
  QHorizontalMetadata,
  QNotFound,
  QPageLoader,
  QStack,
  QText,
  QTitle,
  QVersionMetadata,
  useFeatureFlag,
} from "@qualio/ui-components";
import { DesignElementVersion, FeatureFlags } from "@design-controls/types";
import { DesignElementStatus } from "../../components/DesignElementStatus/DesignElementStatus";
import { useCompareView } from "../../hooks/designElement/useCompareView";
import { CompareVersionSelect } from "./components/CompareVersionSelect";
import { CompareDiff } from "./components/CompareDiff";
import { FIXTURE_OLDER, FIXTURE_VIEWED } from "./compareFixtures";
import {
  isNewerDesignElementVersion,
  toDesignElementVersionLabel,
  toDesignElementVersionNumber,
} from "../../domain/designElementVersion";

export const CompareView: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const viewedVersion = searchParams.get("version");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const features = useFeatureFlag();
  const compareEnabled = Boolean(
    features[FeatureFlags.VERSION_TRACELINK_UI] &&
      features[FeatureFlags.MAJOR_MINOR_VERSION_UI],
  );

  const { isLoading, isNotFound, viewedElement, older, viewed, versions } =
    useCompareView();

  const elementPath = pathname.slice(0, pathname.indexOf("/compare/"));

  const onStopComparing = useCallback(() => {
    navigate(
      viewedVersion ? `${elementPath}/version/${viewedVersion}` : elementPath,
    );
  }, [navigate, elementPath, viewedVersion]);

  const onOlderVersionChange = useCallback(
    (version: DesignElementVersion) => {
      const query = viewedVersion ? `?version=${viewedVersion}` : "";
      navigate(
        `${elementPath}/compare/${toDesignElementVersionLabel(version)}${query}`,
      );
    },
    [navigate, elementPath, viewedVersion],
  );

  const olderVersions = useMemo(
    () =>
      viewed
        ? (versions.data ?? []).filter((version) =>
            isNewerDesignElementVersion(viewed, version),
          )
        : [],
    [versions.data, viewed],
  );

  if (!compareEnabled) {
    return <QNotFound data-cy="not-found" />;
  }

  if (isLoading) {
    return <QPageLoader />;
  }

  if (isNotFound || !viewedElement || !older || !viewed) {
    return <QNotFound data-cy="not-found" />;
  }

  return (
    <QBodyLayout.Default>
      <QHeader>
        <QTitle>
          <QText as="span" color="gray.500">
            {viewedElement.code}
          </QText>{" "}
          <QText as="span" data-cy="compare-title">
            {viewedElement.title}
          </QText>
        </QTitle>
        <QHorizontalMetadata>
          <DesignElementStatus {...viewedElement} />
          <QVersionMetadata
            value={toDesignElementVersionNumber(
              toDesignElementVersionLabel(viewed),
            )}
            data-cy="compare-version"
          />
          <QDateMetadataItem
            label={t("common.metadata.created")}
            value={new Date(viewedElement.created ?? 0)}
          />
          <QDateMetadataItem
            label={t("common.metadata.lastModified")}
            value={new Date(viewedElement.updated ?? 0)}
          />
        </QHorizontalMetadata>
        <QButtonGroup>
          <CompareVersionSelect
            versions={olderVersions}
            selected={older}
            onSelect={onOlderVersionChange}
            data-cy="compare-older-select"
          />
          <QButton
            variant="outline"
            data-cy="stop-comparing"
            onClick={onStopComparing}
          >
            {t("designElementCompare.stopComparing")}
          </QButton>
        </QButtonGroup>
      </QHeader>
      <QStack gap="24px" data-cy="compare-view" minH="1px">
        <CompareDiff older={FIXTURE_OLDER} viewed={FIXTURE_VIEWED} />
      </QStack>
    </QBodyLayout.Default>
  );
};
